import { useContext, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { OpenCallsType } from '../../../contexts/call_manager_context'
import usePreviousProps from '../../../hooks/previous_props_hook'
import {
  getActiveCallForCurrentMember,
  getActiveCallParticipantForCurrentMember,
  getOpenCallsData,
} from '../../../reduxes/slices/calls/selectors'
import { makeGetTopicById } from '../../../reduxes/slices/topics/selectors'
import { CallsRootStoreToSubStoreContext } from './call_manager'
import { CS } from './connection_status_reducer'

export const useMemberCallManager = (memberId: number) => {
  const {
    setActiveMemberId,
    setActiveCall,
    setActiveTopic,
    setActiveParticipant,
    setOpenCalls,
    onDisconnect,
    connectionStatus,
  } = useContext(CallsRootStoreToSubStoreContext)
  const callRecord = useSelector(getActiveCallForCurrentMember)
  const participant = useSelector(getActiveCallParticipantForCurrentMember)
  const prevCallRecord = usePreviousProps(callRecord)
  const prevParticipant = usePreviousProps(participant)
  const getTopic = useMemo(makeGetTopicById, [])
  // eslint-disable-next-line
  // @ts-ignore
  const topic = useSelector((state) => getTopic(state, callRecord?.topic_id))
  const openCallsData = useSelector(getOpenCallsData)

  useEffect(() => {
    if (!setOpenCalls) {
      return
    }
    if (!openCallsData.length) {
      setOpenCalls((prevState: OpenCallsType) => {
        const { [memberId]: _, ...newState } = prevState
        return newState
      })
    } else {
      setOpenCalls((prevState: OpenCallsType) => {
        return { ...prevState, [memberId]: openCallsData }
      })
    }
  }, [memberId, openCallsData, setOpenCalls])

  useEffect(() => {
    if (!setActiveMemberId || !setActiveCall) {
      return
    }

    if (callRecord) {
      setActiveMemberId(memberId)
      setActiveCall(callRecord)
    }
  }, [callRecord, memberId, setActiveCall, setActiveMemberId])

  useEffect(() => {
    if (!setActiveParticipant) {
      return
    }

    if (participant) {
      setActiveParticipant(participant)
    }
  }, [participant, setActiveParticipant])

  useEffect(() => {
    if (setActiveTopic && topic) {
      setActiveTopic(topic)
    }
  }, [topic, setActiveTopic])

  useEffect(() => {
    if (!participant && prevParticipant && setActiveParticipant) {
      setActiveParticipant(null)
    }
  }, [participant, prevParticipant, setActiveParticipant])

  useEffect(() => {
    if (!callRecord && prevCallRecord) {
      if (
        connectionStatus !== CS.LEAVING_CALL &&
        connectionStatus !== CS.DISCONNECTED
      ) {
        onDisconnect({ externalDisconnect: true, playLeaveSound: true })
      }
      setActiveMemberId && setActiveMemberId(null)
      setActiveCall && setActiveCall(null)
      setActiveTopic && setActiveTopic(null)
    }
  }, [
    callRecord,
    connectionStatus,
    onDisconnect,
    prevCallRecord,
    setActiveCall,
    setActiveMemberId,
    setActiveTopic,
  ])
}
